import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setFieldPath } from "./wizardSetField";

// Session A2 — the Sponsored Brands create-wizard slice. Same rules as the SD
// slice: `addCampaign` is untouched, one campaign + one ad group, shaped like
// the §5 payload contract for `POST /amazon/sb/campaign/create`.
//
// ⚠️ `goal` is PAGE_VISIT — SINGULAR. Read off a live campaign in Session A0
// (`sb/v4/campaigns/delete` returns the full resource); the plan's earlier
// PAGE_VISITS was wrong, and `createSBCampaign`'s drop-and-retry ladder would
// have degraded a wrong enum into a silently discarded goal rather than an
// error. The other two values were inferred — C1 confirmed them, and BOTH were
// wrong.
//
// Session C1, from Amazon's own `Sponsored Brands campaign management 4.0`
// spec (`CreateCampaign.goal`): the enum this route accepts is exactly
// **PAGE_VISIT | BRAND_IMPRESSION_SHARE**. `IMPRESSION_SHARE` was missing the
// `BRAND_` prefix, and `SHARE_OF_VOICE` **is not a goal at all** — Amazon's
// third console card ("Reserve share of voice") is a `costType: FIXED_PRICE`
// campaign carrying a `targetedPGDealId` from a separate reservation flow. See
// `SBStep1Goal.tsx`, which holds the whole table and the reason the third card
// is drawn disabled.

export type SbGoal = "PAGE_VISIT" | "BRAND_IMPRESSION_SHARE";

// `CreateCampaign.costType`, same spec: CPC [default] · VCPM · FIXED_PRICE,
// UPPERCASE — unlike Sponsored Display, whose own field is lowercase (`cpc` /
// `vcpm`). The two formats do not share this vocabulary and an adapter that
// borrows SD's casing here is sending a value Amazon has never defined.
// FIXED_PRICE is listed for completeness; it is unreachable without a deal id.
export type SbCostType = "CPC" | "VCPM" | "FIXED_PRICE";
export type SbBudgetType = "DAILY" | "LIFETIME";
export type SbSites = "AMAZON" | "AMAZON_BUSINESS";
export type SbAdFormat = "COLLECTIONS" | "STORE_SPOTLIGHT" | "VIDEO";
export type SbCollectionType = "AUTOMATIC" | "MANUAL";
export type SbLandingPageType = "PRODUCT_LIST" | "STORE";

/**
 * Session E6 — which of the console's three **Ad title** radios is selected.
 *
 * 🔑 **There is no such field on the wire.** `CreateManualCollectionCreative`
 * has exactly one title member (`title`, optional, max 32, *"If not provided,
 * title will be auto-generated."*) and no mode flag anywhere in SB v4, so
 * Amazon's three controls collapse to TWO wire states and this enum exists only
 * to remember which of them the customer meant:
 *
 *   AUTO      "Let us generate the best title for each shopper" [New] — the
 *             preselected one. `title` is OMITTED.
 *   STANDARD  "Choose a standard title" — one of Amazon's own suggestions
 *             (`POST /sb/recommendations/creative/headline`, gap G23) is sent
 *             as `title`.
 *   CUSTOM    "Write your own title" — the typed one is sent as `title`.
 *
 * ⚠️ The two non-AUTO modes keep their values in **separate** fields
 * (`ad.title` for the typed one, `ad.standardTitle` for the chosen one) rather
 * than sharing one box, for C7's reason: switching a radio must never delete
 * work typed under the other one. `sbAdTitleValue()` in `SBStep4Ad` is the one
 * function that turns the three into the one string the launch body carries.
 */
export type SbAdTitleMode = "AUTO" | "STANDARD" | "CUSTOM";
export type SbMatchType = "BROAD" | "PHRASE" | "EXACT";

/**
 * Session C8 — the `AudienceSegmentType` on a `shopperCohortBidAdjustment`.
 *
 * 🔑 **These ids do NOT come from `GET /amazon/sd/audiences`**, which is what
 * the v2 plan said C8 would reuse. Amazon's v4 spec names the source for both
 * values — `ListTargetableEntities` with `adProduct=SPONSORED_BRANDS` — and
 * ends *"Only the audiences retrieved using these filters are usable."* Live on
 * 2026-08-21 the same endpoint answered **3** rows for SPONSORED_BRANDS and
 * **7,048** for SPONSORED_DISPLAY, so the two catalogues are not one list seen
 * twice. `GET /amazon/sb/bid-adjustment-audiences` serves the SB one.
 *
 *   BEHAVIOR_DYNAMIC   Amazon's own brand-engagement cohorts — "New-to-brand
 *                      shoppers", "Purchased brand's product", "Clicked or
 *                      Added brand's product to cart".
 *   SPONSORED_ADS_AMC  audiences the advertiser published from AMC. Zero on
 *                      this account, which is an ACCOUNT fact asked at runtime.
 *
 * ⚠️ The type is a property of the QUERY that found the id, not of the row —
 * `TargetableEntity` carries no segment type — so it is carried here beside the
 * id for C7's reason: a value no shape test can recover must not be inferred.
 */
export type SbAudienceSegmentType = "BEHAVIOR_DYNAMIC" | "SPONSORED_ADS_AMC";

/**
 * Which of Amazon's three audience radios is selected. `NONE` is the default
 * and the one that sends nothing — Amazon's "Don't increase bids".
 */
export type SbAudienceAdjustmentSource = "NONE" | "AMAZON" | "AMC";

export type SbTargetingType = "KEYWORD" | "PRODUCT";

/**
 * Session C4 — one featured product on a MANUAL collection ad.
 *
 * ⚠️ This field was `ad.asins: string[]`, which is what §5's payload contract
 * prints and what `createSBAd` sends. It is rows here, and it is deliberately
 * NOT called `asins` any more: the shared `ProductPicker` returns
 * `{asin, sku, title, imageUrl}` rows, and a field named `asins` holding
 * objects is one `JSON.stringify` away from posting `"[object Object]"` to
 * Amazon. So the wire name is reserved for the wire shape and **C9's adapter
 * emits `asins: ad.products.map((product) => product.asin)`**.
 *
 * `title` / `imageUrl` are UI only — the same arrangement `SdProduct` has had
 * since B3, and stripped by the adapter for the same reason.
 */
export interface SbAdProduct {
  asin: string;
  sku?: string | null;
  title?: string;
  imageUrl?: string;
}

/**
 * Session F5 (D1b) — one tile of a Store spotlight ad.
 *
 * `Subpage` in `sponsored-brands/4-0` has exactly three members and F4 read
 * them off the schema: `url` is the Store page the tile opens, `pageTitle` is
 * the label under it, and `asin` is the product whose image FILLS it. That last
 * one is why this row carries a product and not an asset — a spotlight tile's
 * picture is a product, so the control behind it is the shared `ProductPicker`
 * and not a third uploader beside `LogoPicker` / `VideoPicker`.
 *
 * 🔑 **All three members are formally optional** — `Subpage` has no `required`
 * list at all (F5 re-read the spec) — and only the array's length is stated in
 * numbers: `minItems 3, maxItems 3`. So the wizard refuses a tile with no PAGE
 * and refuses anything other than three tiles, and it does NOT refuse a blank
 * label or a missing product: `createSBAd` omits an empty member rather than
 * sending it, and refusing here would refuse a body Amazon may well accept
 * (F4's rule, and the reason the logo and the headline are blocked in the
 * WIZARD rather than in the helper).
 *
 * ⚠️ The array is held at its full length of `SB_STORE_SPOTLIGHT_SUBPAGES`
 * rows, empty ones included, so that tile 2 keeps its own slot while tile 1 is
 * still blank. "How many are chosen" is therefore the count of rows with a
 * `url`, never `subpages.length` — see `sbChosenSubpages` in `SBStep4Ad`.
 */
export interface SbSubpageRow {
  /**
   * UI only — which row of `GET /amazon/sb/store-pages` this tile is showing.
   * The wire field is `url`; this is what keeps the dropdown pointing at the
   * customer's choice after they step away and back, exactly as
   * `landingPageStorePageId` does for the ad's own landing page (E8).
   */
  storePageId: string | null;
  /** `Subpage.pageTitle` — the tile's label. No maxLength in the schema. */
  pageTitle: string;
  /**
   * `Subpage.url` — Amazon's own `storePageUrl`, copied verbatim off the page
   * list and never composed from an id. E8's finding, reused: the Brand Home
   * API's string IS what Sponsored Brands wants here.
   */
  url: string;
  /** `Subpage.asin` — the product whose image fills the tile. */
  asin: string;
  /** UI only — the picker's row, so the tile can show what it chose. */
  asinTitle?: string;
  asinImageUrl?: string;
}

export interface SbKeywordRow {
  id: string; // UI only
  keywordText: string;
  matchType: SbMatchType;
  bid: number | null; // required on every row — SB has no ad-group defaultBid
}

/**
 * ⚠️ Session C6 removed `suggestedBid` from this row and from
 * `SbProductTargetRow` below. Amazon's suggestion is NOT part of the launch
 * payload, and this slice IS the launch payload (§5) — so the step holds it in
 * component state, keyed by row id, exactly as `SDStep5Targeting` has held G4's
 * answers since B14. Keeping it here would have put a value nobody sends into
 * the store and opened the fetch → dispatch → refetch loop that arrangement
 * invites. C7 does the same with its product-target prices.
 */

export interface SbNegativeKeywordRow {
  id: string; // UI only
  keywordText: string;
  matchType: Exclude<SbMatchType, "BROAD">;
}

/**
 * Session E7 — one of the console's **Targeting groups** (`POST /sb/themes`,
 * gap G22). Amazon draws two rows at the top of the keyword card's Suggested
 * tab, each with Match type `Theme` and its own suggested bid.
 *
 * ⚠️ **`ThemeType` is not an enum in Amazon's spec.** It is a bare
 * `type: string` whose description names the two values below and then adds
 * *"Additional theme types may be added in the future"*. The union here is the
 * two this wizard offers — a UI vocabulary, deliberately narrower than the
 * wire's — and nothing in the chain coerces a value against it, so a third type
 * Amazon adds later costs one entry in `SB_THEME_TYPES` rather than a hunt
 * through three files for a `.toUpperCase()`.
 */
export type SbThemeType =
  | "KEYWORDS_RELATED_TO_YOUR_BRAND"
  | "KEYWORDS_RELATED_TO_YOUR_LANDING_PAGES";

/**
 * 🔑 **`themeType` IS the id.** Amazon allows *"only one target … for each
 * themeType per adGroup"*, so a second row of the same type is not a duplicate
 * to warn about — it is unrepresentable. That is why this row carries no `id`
 * the way every other list in this slice does, and why the UI's Add button
 * disappears once a type is on the list.
 *
 * `bid` is required by `SBCreateThemesRequest` itself, not merely by SB's
 * missing ad-group default — the one row type in this wizard where the schema
 * says so outright.
 */
export interface SbThemeRow {
  themeType: SbThemeType;
  bid: number | null;
}

/**
 * One positive product target (`POST /sb/targets`).
 *
 * `type` is Session C7's addition and it is not decoration: the backend infers
 * the predicate with `sdExpressionType`, whose test is "does this look like a
 * 10-character ASIN?" and whose fallback is `asinCategorySameAs`. That works
 * for an ASIN and it works for a bare browse-node id — but only while the
 * value is bare, and the row contract prefixes ASINs (`ASIN=B0…`). Carrying
 * the type means the wizard never depends on a shape test to tell a product
 * from a category, which is the same reason `SbNegativeProductTargetRow` below
 * carries one.
 *
 * ⚠️ `asinCategorySameAs` is a member of SB's `ProductPredicateType` (read from
 * the sponsored-brands/3-0 spec in C7) — so SB category targeting is real, and
 * so are the brand / price / rating REFINEMENTS beside it. Session F6 built two
 * of the three; see `SbTargetRefinements` below for why the third is missing
 * from the TYPE and not merely from the card.
 */
export type SbProductTargetType = "asinSameAs" | "asinCategorySameAs";

/**
 * Session F6, gap G20 — what narrows a CATEGORY target.
 *
 * 🔑 **There is no `brands` member, and its absence is the design.** SD's
 * refinement shape has one (`SdContextualTarget["refinements"]`), because SD
 * once expected to fill it. On this account nothing can: no endpoint issues a
 * brand id, which B5 measured live and G3 records, so the wizard draws no Brands
 * control and `createSBProductTargets` calls the shared predicate builder with
 * no brand argument. A member nobody can fill is a member a later session fills
 * wrongly — so the type does not have one, and adding it is a deliberate act
 * rather than an oversight corrected.
 *
 * ⚠️ **Both ends of a range or neither.** The wizard builds only the `Between`
 * forms of the two predicates, so a one-sided range is not narrower targeting —
 * it is a refinement dropped on the way out, leaving the whole category
 * targeted at the customer's bid behind a clean `200`. `refinementRangeErrors`
 * refuses it on the card and `sbRefinementProblem` refuses it again at the
 * gateway. (Amazon's own `ProductPredicateType` does list the one-sided
 * predicates — `asinPriceGreaterThan` and its three siblings — so lifting this
 * limit is a build, not a probe. F6 read them out of the 3-0 spec.)
 *
 * ⚠️ **`min` must be strictly BELOW `max`**, not merely not-above: the SD 3.0
 * spec fixes the wire format of both `Between` strings as *"'double-double'
 * where the first double must be smaller than the second double"*. An
 * equal-ended range is a refused target create, not a very narrow one.
 */
export interface SbTargetRefinementRange {
  min: number | null;
  max: number | null;
}

export interface SbTargetRefinements {
  priceRange?: SbTargetRefinementRange;
  rating?: SbTargetRefinementRange;
}

export interface SbProductTargetRow {
  id: string; // UI only
  /** "ASIN=B0…" for a product; the bare browse-node id for a category. */
  target: string;
  type: SbProductTargetType;
  label?: string; // UI only
  bid: number | null;
  /**
   * F6 — present only on a CATEGORY row, and omitted entirely rather than held
   * as an empty object when nothing is refined. `buildSbLaunchPayload` gates the
   * key on the same fact, which is what keeps an unrefined launch byte-identical
   * to the one C7 shipped.
   */
  refinements?: SbTargetRefinements;
}

/**
 * Session C5 — one negative product target (`POST /sb/negativeTargets`).
 *
 * ⚠️ This was `SbProductExclusionRow`, and the rename is the finding. The plan
 * had ONE "product exclusions" feature routed two ways — the creative's
 * `asinExclusions` under Automatic, this endpoint otherwise. The two specs say
 * they are two different features that both exclude something:
 *
 *   ad.excludedProducts  → CreateAutoCollectionCreative.asinExclusions
 *                          which of YOUR OWN products Amazon may put IN the ad
 *   these rows           → POST /sb/negativeTargets, tagged "Negative product
 *                          targeting" — which brands and detail pages your ad
 *                          must NOT APPEAR on
 *
 * Neither substitutes for the other, and they apply to different collection
 * types for different reasons: `asinExclusions` exists only on the automatic
 * creative, while negative targeting is a property of the campaign's targeting
 * and is available whichever creative it carries. **C7 owns the UI** — it
 * belongs beside product targeting, which is where Amazon's console draws it.
 *
 * `type` is carried rather than inferred because `SBNegativeExpression` is a
 * two-value enum — `asinSameAs` | `asinBrandSameAs` — and a brand id is a
 * number, so no shape test can tell it from anything else. Note what is NOT in
 * that enum: `asinCategorySameAs`. A category can be targeted and cannot be
 * negatively targeted.
 */
export type SbNegativeTargetType = "asinSameAs" | "asinBrandSameAs";

export interface SbNegativeProductTargetRow {
  id: string; // UI only
  type: SbNegativeTargetType;
  /** The bare ASIN or brand id — no "ASIN=" prefix; `type` carries that. */
  value: string;
  label?: string; // UI only
}

export interface SbCampaignState {
  currentStep: number;
  profileId: string | null;
  portfolioId: string | null;
  /**
   * Session C2 — written by the Settings card's `onPortfolioLoad`, never
   * derived here. It records whether the portfolio list came back WITHOUT a
   * "No Portfolio" entry (a SUB_USER's list does not carry one), which is the
   * only thing that makes the field required on SB. Kept in the slice rather
   * than in step state so C9's review blockers read the same fact step 2
   * validates on — the SD slice holds it for the same reason.
   */
  portfolioRequired: boolean;
  /**
   * Session C9 — `portfolioRequired`'s twin, written by the Settings card from
   * the profile's own marketplace and read by `sbLaunchBlockers`.
   *
   * UI only; `buildSbLaunchPayload` names its keys, so it never travels.
   *
   * ⚠️ It exists for D4's reason rather than for a new one. `validateSbSettings`
   * needs to know whether this marketplace offers Amazon Business at all, and
   * only the mounted card can measure it — so a review screen that guessed
   * `true` would let the container's `stepValid` kill Submit while the blocker
   * list, the one thing that exists to say WHY, stayed silent. One fact,
   * measured once, read in two places.
   *
   * Defaults to TRUE: a country we cannot read is not a country we can rule
   * out, which is the same default the card itself takes.
   */
  amazonBusinessAvailable: boolean;
  goal: SbGoal;
  campaign: {
    name: string;
    budget: number | null;
    budgetType: SbBudgetType;
    startDate: string; // YYYY-MM-DD
    endDate: string | null;
    // Written by Step 1 from the SAME table row as `goal`, never chosen on its
    // own — B4's rule for SD, and for the same reason: two independently
    // settable fields that must agree will eventually disagree.
    costType: SbCostType;
    /**
     * Amazon's ASIN-shaped brand id — the picker's identity and what step 2
     * gates on. A VENDOR profile's brands carry no entity id at all, so the
     * entity id cannot be the thing that says "a brand was chosen".
     */
    brandId: string;
    brandEntityId: string; // may be "" on a vendor profile
    brandName: string; // UI only — the label behind brandId
    sites: SbSites;
    /**
     * Session C8 — Amazon's Campaign bid adjustments card, both tabs.
     *
     * Maps onto the v4 `Bidding` object, whose two arrays are independent:
     *
     *   placements → bidAdjustmentsByPlacement  (maxItems 3, **-99 … 900**)
     *   audience   → shopperCohortBidAdjustments (maxItems 1, **0 … 900**)
     *
     * ⚠️ The two percentage ranges are NOT the same. A placement bid can be
     * adjusted DOWN; an audience one cannot, and a shared validator would let a
     * negative cohort through to a 400.
     *
     * ⚠️ `bidOptimization` is deliberately not held here. It is not a customer
     * choice — it is the flag that makes the placement half apply at all
     * (`true`, Amazon's default, means `bidAdjustmentsByPlacement` is ignored),
     * so `sbBidding()` in AmazonHelper emits it with the placements and nowhere
     * else. A field here would let a campaign that adjusted only an audience
     * silently switch Amazon's placement optimisation off.
     */
    bidAdjustments: {
      /** Percent, -99 … 900. `null` is "no adjustment", not 0. */
      placements: {
        topOfSearch: number | null;
        productPages: number | null;
        restOfSearch: number | null;
      };
      audience: {
        source: SbAudienceAdjustmentSource;
        audienceId: string | null;
        /** Carried, never inferred — see `SbAudienceSegmentType`. */
        audienceSegmentType: SbAudienceSegmentType | null;
        label?: string; // UI only — the audience's display name
        /** Percent, 0 … 900. `null` is "not set yet", and 0 is a real value. */
        percentage: number | null;
      };
    };
    /**
     * Session F7c, gap G25 — Amazon's **Cost control** card, drawn under the
     * Campaign bid adjustments card on the CPC goal only.
     *
     * 🔑 **It is not a campaign field, and the slice says so by living beside
     * `bidAdjustments` rather than beside `budget`.** `CreateCampaign` has
     * sixteen properties and no cost-control member: what the checkbox writes
     * is a separate resource — `POST /sb/rules/optimization`, created against a
     * campaign that already exists, as a fifth call after the ad. See
     * `createSBOptimizationRule` in `AmazonHelper.js`.
     *
     * Same shape as the SD slice's `campaign.costControl` (B4), deliberately:
     * two wizards, one control the customer recognises, one pair of field
     * names. `enabled` is the checkbox and `value` is the target — `null` is
     * "checked but not yet typed", which the validator catches, and 0 is not a
     * legal target rather than a default.
     *
     * ⚠️ **Kept across a goal change, never cleared.** C7's rule: a control the
     * customer filled in survives a switch. The GOAL gate lives in
     * `sbCostControlCampaignFields`, where the payload is decided, so a target
     * typed under CPC cannot ride out on a vCPM campaign whose console has no
     * such card.
     */
    costControl: { enabled: boolean; value: number | null };
  };
  adGroup: { name: string };
  ad: {
    name: string;
    format: SbAdFormat;
    collectionType: SbCollectionType | null;
    brandName: string;
    /**
     * `brandLogoAssetID` on BOTH collection creatives (capital `ID` — A0's live
     * run took it on the first rung). Optional on both.
     */
    logoAssetId: string | null;
    logoUrl: string | null; // UI only — the picker's thumbnail
    logoName: string | null; // UI only — what the review step calls the logo
    /**
     * Session C3 — this field was called `headline` and carried "≤ 50 chars".
     * Both were the DEPRECATED `sb/v4/ads/productCollection` resource's, which
     * is the only collection creative that has ever had a `headline`. The
     * current resources:
     *
     *   manualCollection  `title`  optional, maxLength **32**,
     *                              *"If not provided, title will be
     *                              auto-generated."*
     *   autoCollection    — no title or headline field of any kind.
     *
     * `createSBAd` has read `ad.title` since the manualCollection contract was
     * settled, so the slice was the last place still naming the dead field. See
     * `SBStep3AdGroup.tsx`, which holds the whole per-collection-type contract.
     *
     * ⚠️ Session E6 — this is now the **CUSTOM** radio's box alone, not "the
     * title". What actually travels is `sbAdTitleValue()`'s answer, which is
     * empty on AUTO and `standardTitle` on STANDARD.
     */
    title: string;
    /** E6 — which of Amazon's three title radios. See `SbAdTitleMode`. */
    titleMode: SbAdTitleMode;
    /**
     * E6 — the suggestion picked under "Choose a standard title", held apart
     * from `title` so that switching radios cannot delete either one. The
     * suggestion's `headlineId` is deliberately NOT kept: no creative schema in
     * SB v4 has a member for it, so it would be a stored value nothing sends.
     */
    standardTitle: string | null;
    /**
     * Session F3 — the BRAND VIDEO's headline, and deliberately NOT `ad.title`.
     *
     * `CreateBrandVideoCreative.headline` is a different member of a different
     * creative with a different limit (50, not 32 — see
     * `SB_BRAND_VIDEO_HEADLINE_MAX`), and `ad.title` is no longer a text box at
     * all: since E6 it is the CUSTOM radio's half of a three-state control that
     * `sbAdTitleValue()` collapses. Pointing the video's plain box at that field
     * would mean either dragging E6's radios onto a format that has none, or
     * writing a second, silent path into a field one function is supposed to
     * own. Two fields is the cheaper honesty — and it also means switching ad
     * format never has to delete either one's text.
     *
     * F2 already made the backend read both names (`sbCreativeInputs` carries
     * `title` and `headline` and the contract decides which travels), so only
     * ever one of them reaches Amazon.
     */
    headline: string;
    /**
     * Session F3 — `videoAssetIds` on BOTH video creatives, an Asset Library
     * `assetId` and the same currency as `logoAssetId`. Amazon's member is an
     * array of exactly one (`minItems 1, maxItems 1`); the slice holds the one,
     * and `createSBAd` is what wraps it.
     */
    videoAssetId: string | null;
    /**
     * UI only, and the same five-field shape the logo has one row up, for the
     * same reason: `POST /assets/register` answers with no URL, so a freshly
     * uploaded video is previewed from an object URL until Amazon's library
     * indexes it (B10/D3a). The four measurements are what the picker's meta
     * line prints — Amazon re-derives all of them from the file itself.
     */
    videoUrl: string | null;
    videoName: string | null;
    videoBytes: number | null;
    videoWidth: number | null;
    videoHeight: number | null;
    /**
     * D3's finding, reused whole: Amazon's register response carries no
     * duration, so this is measured in the browser before the upload and is the
     * only place it survives.
     */
    videoDurationSeconds: number | null;
    /**
     * `CreateManualCollectionCreative.asins` — REQUIRED, minItems 3, maxItems
     * 10 (C3 read the schema; the old "0–3" was the DEPRECATED
     * productCollection's). Rows rather than strings — see `SbAdProduct`.
     */
    products: SbAdProduct[];
    /**
     * autoCollection only — `CreateAutoCollectionCreative.asinExclusions`, 0 to
     * 1000. An automatic collection picks its own ASINs, so the only product
     * control it offers is which ones to keep OUT of the ad.
     *
     * ⚠️ Session C5 renamed this from `asinExclusions`, on exactly C4's
     * reasoning for `ad.asins` → `ad.products`: the shared `ProductPicker`
     * returns `{asin, sku, title, imageUrl}` rows and this holds them, so that
     * the excluded list can show a product's title and thumbnail the way the
     * featured list does. A field NAMED for the wire must hold the wire's
     * shape, so the wire name is reserved for it and **C9's adapter emits
     * `asinExclusions: ad.excludedProducts.map((product) => product.asin)`**.
     *
     * Not to be confused with `targeting.negativeProductTargets` — see
     * `SbNegativeProductTargetRow`, which explains why they are two features.
     */
    excludedProducts: SbAdProduct[];
    /**
     * Session F5 — `CreateStoreSpotlightCreative.subpages`, Store spotlight's
     * whole content and the one creative member no other SB format has.
     *
     * Held at full length with empty rows for the reason `SbSubpageRow` gives.
     * `buildSbLaunchPayload` drops the empty ones and sends the rest only when
     * the contract says the resource has the member, so an untouched slice adds
     * nothing to a collections or a video body.
     */
    subpages: SbSubpageRow[];
    landingPageType: SbLandingPageType;
    /**
     * The wire field, and the ONLY one of these five C9's adapter sends:
     * `BrandCollectionLandingPage.url`. Since E8 it is never typed — it is
     * copied verbatim from the chosen page's `storePageUrl`, which is what
     * Amazon's own Brand Home API returns for it.
     */
    landingPageUrl: string | null; // only when landingPageType === "STORE"
    /**
     * E8, gap G24 — what the two dropdowns are SHOWING. None of these four
     * reach Amazon; they exist so a customer who steps away and back finds
     * their Store and page still selected, and so the Review step can print
     * *"Evolution Outdoor / Home page"* instead of a 60-character GUID URL.
     *
     * The names are stored beside the ids rather than re-fetched, for the
     * reason C4 copied the brand name instead of re-reading it: a second read
     * can disagree with the selection the customer actually made.
     */
    landingPageStoreId: string | null;
    landingPageStoreName: string | null;
    landingPageStorePageId: string | null;
    landingPageStorePageName: string | null;
  };
  targeting: {
    type: SbTargetingType;
    keywords: SbKeywordRow[];
    /**
     * E7, gap G22 — the console's "Targeting groups". Keyword-family targeting,
     * so it travels only under `type: "KEYWORD"` (C7's rule: the type decides,
     * never which array is non-empty) and it is additive rather than an
     * alternative — Amazon draws these rows ABOVE the keyword table, not
     * instead of it.
     */
    themes: SbThemeRow[];
    products: SbProductTargetRow[];
    negativeKeywords: SbNegativeKeywordRow[];
    /** C7's — see `SbNegativeProductTargetRow`. Backend built in C5. */
    negativeProductTargets: SbNegativeProductTargetRow[];
  };
}

// collectionType starts null on purpose: Amazon renders Automatic/Manual as an
// un-preselected radio pair, and C3 must not silently commit the user to the
// one creative type we have proven (manualCollection). C3 kept it that way —
// the two types route to two different endpoints taking two different creative
// bodies, so a default here is a default *ad*, not a default control position.
const initialState: SbCampaignState = {
  currentStep: 1,
  profileId: null,
  portfolioId: null,
  portfolioRequired: false,
  amazonBusinessAvailable: true,
  goal: "PAGE_VISIT",
  campaign: {
    name: "",
    budget: null,
    budgetType: "DAILY",
    startDate: "",
    endDate: null,
    costType: "CPC", // Amazon's own default, and PAGE_VISIT's pair.
    brandId: "",
    brandEntityId: "",
    brandName: "",
    sites: "AMAZON",
    bidAdjustments: {
      placements: {
        topOfSearch: null,
        productPages: null,
        restOfSearch: null,
      },
      audience: {
        source: "NONE",
        audienceId: null,
        audienceSegmentType: null,
        percentage: null,
      },
    },
    // F7c — off, and `value` null rather than seeded. Amazon's console draws
    // the box empty and fills its two grey lines from a recommendation, so a
    // number here would be ours pretending to be Amazon's.
    costControl: { enabled: false, value: null },
  },
  adGroup: { name: "" },
  ad: {
    name: "",
    format: "COLLECTIONS",
    collectionType: null,
    brandName: "",
    logoAssetId: null,
    logoUrl: null,
    logoName: null,
    title: "",
    // E6 — Amazon's own preselect, and the one that sends nothing.
    titleMode: "AUTO",
    standardTitle: null,
    // Session F3 — the brand video's own text and its video. Empty is the
    // Collections default too, so an untouched slice builds exactly the body
    // C9's unrun live tests were written against.
    headline: "",
    videoAssetId: null,
    videoUrl: null,
    videoName: null,
    videoBytes: null,
    videoWidth: null,
    videoHeight: null,
    videoDurationSeconds: null,
    products: [],
    excludedProducts: [],
    // F5 — empty, not three blank rows: the card pads to its own length when it
    // draws, and an empty array here keeps a collections slice exactly the shape
    // it was before this session.
    subpages: [],
    landingPageType: "PRODUCT_LIST",
    landingPageUrl: null,
    landingPageStoreId: null,
    landingPageStoreName: null,
    landingPageStorePageId: null,
    landingPageStorePageName: null,
  },
  targeting: {
    type: "KEYWORD",
    keywords: [],
    themes: [],
    products: [],
    negativeKeywords: [],
    negativeProductTargets: [],
  },
};

const createSbCampaignSlice = createSlice({
  name: "createSbCampaign",
  initialState,
  reducers: {
    setField: (
      state,
      action: PayloadAction<{ path: string; value: unknown }>
    ) => {
      setFieldPath(state, action.payload.path, action.payload.value);
    },
    setStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    reset: () => initialState,
  },
});

export const { setField, setStep, reset } = createSbCampaignSlice.actions;
export const sbInitialState = initialState;
export default createSbCampaignSlice.reducer;
